import { useState } from 'react'
import { toast } from 'react-hot-toast'
import { PencilIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUpdateStorageVolume } from '@/services/storage'
import { WebrpcError } from '@/services/inference.gen'

const FormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  is_public: z.boolean().default(false),
})

type FormValues = z.infer<typeof FormSchema>

type EditStorageDialogProps = {
  volumeId: number
  volumeName: string
  isPublic: boolean
}

export const EditStorageDialog = ({
  volumeId,
  volumeName,
  isPublic,
}: EditStorageDialogProps) => {
  const [open, setOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: volumeName,
      is_public: isPublic,
    },
  })

  const { mutateAsync, isPending } = useUpdateStorageVolume()

  const onSubmit = async (data: FormValues) => {
    try {
      await mutateAsync({
        volume_id: volumeId,
        name: data.name,
        is_public: data.is_public,
      })
      setOpen(false)
      toast.success('Storage volume updated successfully')
    } catch (error) {
      console.error(error)
      if (error instanceof WebrpcError) {
        toast.error(JSON.stringify(error))
      } else {
        toast.error('Failed to update storage volume')
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <PencilIcon className="h-4 w-4" /> Edit
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl">Edit Storage Volume</DialogTitle>
          <DialogDescription>
            Update the properties of your storage volume
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 py-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="my-storage" {...field} />
                  </FormControl>
                  <FormDescription>
                    A unique name for your storage volume
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_public"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between space-y-0 rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">
                      Organization-wide access
                    </FormLabel>
                    <FormDescription>
                      Make this storage volume readable by all users in your
                      organization
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button disabled={isPending} type="submit">
                {isPending ? 'Updating...' : 'Update Storage'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
